import React, { useId } from "react"
import { Checkbox, Input, Segmented, Select, Switch } from "antd"
import { QuestionnaireNames } from "utils/questionnaireNames"
import { TemplateInput } from "types/template.types"
import { InputState, allowsExceptions, nameWarning } from "./templateInputs"
import { inputLabelAsWords } from "./templateText"

interface TemplateInputFieldProps {
  input: TemplateInput
  state: InputState
  onChange: (next: InputState) => void
  /** The names the questionnaire already uses */
  names: QuestionnaireNames
  /** The NAME values of the blocks already in the tray */
  stagedNames: string[]
  /** The wizard's current preview text (a NAME is checked only when it defines it) */
  merged: string
  /** The exceptions offered as checkboxes (inputs that allow exceptions) */
  exceptionChoices: string[]
  /** A required input left empty */
  missing: boolean
}

/** Each name once, in order. */
const optionsOf = (...lists: string[][]) =>
  Array.from(new Set(lists.flat())).map((name) => ({ label: name, value: name }))

/**
 * One input of the template wizard, with the control its kind asks for:
 * TEXT and NAME an input, LIST a textarea, EXCEPTIONS checkboxes, PIPE a
 * typed name or an earlier answer, LISTREF typed items or an existing list,
 * OPTION a switch. Shows "Required" under an empty required field and, for a
 * NAME, a warning when the name is taken.
 *
 * @param props - The component props
 * @returns The field
 */
const TemplateInputField: React.FC<TemplateInputFieldProps> = ({
  input,
  state,
  onChange,
  names,
  stagedNames,
  merged,
  exceptionChoices,
  missing,
}) => {
  const id = useId()
  const label = input.instruction.trim() || inputLabelAsWords(input.label)
  const set = (patch: Partial<InputState>) => onChange({ ...state, ...patch })

  if (input.type === "OPTION") {
    return (
      <div className="tpl-field tpl-field--switch">
        <Switch
          id={id}
          size="small"
          checked={state.on}
          onChange={(on) => set({ on })}
        />
        <label htmlFor={id} className="tpl-field-label">
          {label}
        </label>
      </div>
    )
  }

  const textInput = (
    <Input
      id={id}
      size="small"
      value={state.text}
      onChange={(e) => set({ text: e.target.value })}
      status={missing ? "error" : undefined}
    />
  )
  const textArea = (
    <Input.TextArea
      id={id}
      size="small"
      value={state.text}
      autoSize={{ minRows: 3, maxRows: 8 }}
      onChange={(e) => set({ text: e.target.value })}
      status={missing ? "error" : undefined}
    />
  )
  const picker = (options: { label: string; value: string }[], empty: string) => (
    <Select
      id={id}
      size="small"
      className="tpl-field-select"
      showSearch
      value={state.pick || undefined}
      options={options}
      placeholder="Choose"
      notFoundContent={empty}
      onChange={(pick: string) => set({ pick })}
      status={missing ? "error" : undefined}
    />
  )
  const modeSwitch = (typeLabel: string, pickLabel: string) => (
    <Segmented
      size="small"
      className="tpl-field-mode"
      value={state.mode}
      options={[
        { label: typeLabel, value: "type" },
        { label: pickLabel, value: "pick" },
      ]}
      onChange={(mode) => set({ mode: mode as InputState["mode"] })}
    />
  )
  const checkboxes = (
    <div role="group" aria-label={`${label}: exceptions`}>
      <Checkbox.Group
        className="tpl-field-checks"
        value={state.ticked}
        options={exceptionChoices}
        // Keep the order the choices are shown in.
        onChange={(ticked) =>
          set({ ticked: exceptionChoices.filter((c) => ticked.includes(c)) })
        }
      />
    </div>
  )

  let control: React.ReactNode
  switch (input.type) {
    case "LIST":
      control = textArea
      break
    case "EXCEPTIONS":
      control =
        exceptionChoices.length > 0 ? (
          checkboxes
        ) : (
          <div className="tpl-field-hint">No exceptions to choose from.</div>
        )
      break
    case "PIPE":
      control = (
        <>
          {modeSwitch("Type a name", "Pipe an earlier answer")}
          {state.mode === "pick"
            ? picker(
                optionsOf(names.questions, names.hidden),
                "No questions yet"
              )
            : textInput}
        </>
      )
      break
    case "LISTREF":
      control = (
        <>
          {modeSwitch("Type the items", "Use a list from this questionnaire")}
          {state.mode === "pick"
            ? picker(optionsOf(names.lists, names.rangeLists), "No lists yet")
            : textArea}
        </>
      )
      break
    default:
      control = textInput
  }

  const warning =
    input.type === "NAME" ? nameWarning(state.text, names, stagedNames, merged) : null
  const typedList =
    input.type === "LIST" || (input.type === "LISTREF" && state.mode === "type")

  return (
    <div className="tpl-field">
      {input.type === "EXCEPTIONS" ? (
        <div className="tpl-field-label">{label}</div>
      ) : (
        <label htmlFor={id} className="tpl-field-label">
          {label}
        </label>
      )}
      {control}
      {typedList && <div className="tpl-field-hint">One item per line</div>}
      {input.type !== "EXCEPTIONS" &&
        allowsExceptions(input) &&
        exceptionChoices.length > 0 && (
          <div className="tpl-field-exc">
            <div className="tpl-field-sublabel">Exceptions</div>
            {checkboxes}
          </div>
        )}
      {missing && <div className="tpl-field-error">Required</div>}
      {warning && <div className="tpl-field-warn">{warning}</div>}
    </div>
  )
}

export default TemplateInputField
